import React, { useMemo } from 'react';
import Container from 'src/components/shared/Container';
import Seo from 'src/components/Seo';
import PlacementOptOutSection from 'src/components/support/PlacementOptOutSection';
import HelpdeskSection from 'src/components/support/HelpdeskSection';
import SupportQuestionsSection from 'src/components/support/SupportQuestionsSection';
import MyTicketsSection from 'src/components/support/MyTicketsSection';
import SupportModal from 'src/components/support/SupportModal';
import { Breadcrumb } from 'flowbite-react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import seoConfig from '../seo.json';

const Support = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const seoInfo = seoConfig['support'] || { title: 'Support | LevelUp', description: 'Support page for LevelUp' };

    // URL as source of truth: tickets=ticketlisting → Support Tickets tab; otherwise Help
    const ticketsListingParam = searchParams.get('tickets') === 'ticketlisting';
    const activeTab: 'help' | 'tickets' = ticketsListingParam ? 'tickets' : 'help';

    // Show modal when: (1) modal=search/fallback, (2) ticket details (step=ticketdetails&ticketId=...), or (3) category+subcategory (Help flow)
    const stepTicketDetails = searchParams.get('step') === 'ticketdetails';
    const urlTicketId = searchParams.get('ticketId');
    const showTicketDetailsInModal = !!(stepTicketDetails && urlTicketId);
    const categoryParam = searchParams.get('category');
    const subcategoryParam = searchParams.get('subcategory');
    const showSupportModal = useMemo(() => {
        const modal = searchParams.get('modal');
        if (modal === 'search' || modal === 'fallback') return true;
        if (showTicketDetailsInModal) return true;
        // Help flow: category+subcategory opens modal (handled by SupportQuestionsSection opening modal via URL)
        return !!(categoryParam && subcategoryParam);
    }, [searchParams, showTicketDetailsInModal, categoryParam, subcategoryParam]);

    const handleCloseSupportModal = () => {
        const params = new URLSearchParams(location.search);
        params.delete('modal');
        params.delete('step');
        params.delete('ticketId');
        params.delete('category');
        params.delete('subcategory');
        params.delete('questionId');
        const qs = params.toString();
        navigate(qs ? `/support?${qs}` : '/support', { replace: true });
    };

    return (
        <div className="pb-[200px] md:pb-[180px]">
            <Seo title={seoInfo.title} description={seoInfo.description} />
            <Container className="w-[92%] max-w-[1280px] pt-2 md:pb-2 pb-10 sm:w-[96%] font-inter">
                <div className="md:mt-6 mb-6">
                    <Breadcrumb aria-label="support-breadcrumb">
                        <Breadcrumb.Item
                            onClick={() => navigate('/')}
                            className="cursor-pointer"
                        >
                            <p className="font-inter text-sm font-medium dark:text-[#EBEBF0] text-[#555770]">
                                Home
                            </p>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <p className="font-inter text-sm font-medium dark:text-[#EBEBF0] text-[#555770]">
                                Support
                            </p>
                        </Breadcrumb.Item>
                    </Breadcrumb>
                </div>

                {/* Top Sections - Always Visible */}
                <div className="flex flex-col gap-6 mb-6">
                    {/* Placement Opt-out - Always at top */}
                    <PlacementOptOutSection />

                    {/* Helpdesk - Second at top */}
                    <HelpdeskSection />
                </div>

                {/* Floating banner: New Support Feature in Testing - yellow/pink, font-inter for consistency */}
                <div className="font-inter mb-4 rounded-lg border border-pink-200 dark:border-pink-600 bg-amber-50 dark:bg-amber-900/30 px-4 py-3 text-sm font-normal text-gray-800 dark:text-gray-200">
                    <p className="flex items-center gap-2">
                        <span aria-hidden>🚀</span>
                        New Support Feature in Testing
                    </p>
                    <p className="mt-1">
                        We&apos;re currently testing this feature to improve your support experience. Some information may change while we refine it and review for accuracy.
                    </p>
                </div>

                {/* Tabs Section - URL-driven: tickets=ticketlisting shows Support Tickets */}
                <div className="mb-6">
                    <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
                        <button
                            onClick={() => {
                                const next = new URLSearchParams(searchParams);
                                next.delete('tickets');
                                next.delete('tab');
                                next.delete('step');
                                next.delete('ticketId');
                                const qs = next.toString();
                                navigate(qs ? `/support?${qs}` : '/support', { replace: true });
                            }}
                            className={`px-4 py-2 font-medium text-sm transition-colors ${activeTab === 'help'
                                ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                        >
                            Help
                        </button>
                        <button
                            onClick={() => {
                                const next = new URLSearchParams(searchParams);
                                next.set('tickets', 'ticketlisting');
                                // Default to unresolved when switching to ticket listing
                                if (!next.has('tab')) next.set('tab', 'unresolved');
                                next.delete('step');
                                next.delete('ticketId');
                                navigate(`/support?${next.toString()}`, { replace: true });
                            }}
                            className={`px-4 py-2 font-medium text-sm transition-colors ${activeTab === 'tickets'
                                ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                        >
                            Support Tickets
                        </button>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="mt-4">
                    {activeTab === 'help' ? (
                        <SupportQuestionsSection onOpenCreateTicket={() => navigate('/support?modal=fallback')} />
                    ) : (
                        <MyTicketsSection />
                    )}
                </div>

                {/* Support Modal - 3 versions: fallback (ticket form), search (by batch), questions (category+subcategory) */}
                <SupportModal show={showSupportModal} onClose={handleCloseSupportModal} />
            </Container>
        </div>
    );
};

export default Support;
